import React, { useState, useCallback } from 'react';
import { Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useP9315Query } from './hooks/useP9315Query';
import { useP9315Lookup } from './hooks/useP9315Lookup';
import { buildPrintHtml } from './helpers';
import FilterPanel from './components/FilterPanel';
import ResultTable from './components/ResultTable';
import './P9315Form.css';

const P9315Form: React.FC = () => {
  const {
    loading, data, queried, noData,
    daysInMonth, queryYear, queryMonth,
    runQuery, reset,
  } = useP9315Query();
  const { deptFromName, deptToName, onDeptFromBlur, onDeptToBlur, resetNames } = useP9315Lookup();

  // Filter state - default to current year/month
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');

  const handleQuery = useCallback(() => {
    runQuery({
      year,
      month,
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
    });
  }, [year, month, deptFrom, deptTo, runQuery]);

  const handleReset = useCallback(() => {
    const n = new Date();
    setYear(n.getFullYear());
    setMonth(n.getMonth() + 1);
    setDeptFrom('');
    setDeptTo('');
    resetNames();
    reset();
  }, [reset, resetNames]);

  const handlePrint = useCallback(() => {
    if (!data || data.length === 0) return;
    const html = buildPrintHtml(data, queryYear, queryMonth, daysInMonth);
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  }, [data, queryYear, queryMonth, daysInMonth]);

  return (
    <PermissionGuard module={Modules.PW9315} action="query">
      <div className="p9315-container">
        <div className="p9315-header">
          <h2>部門個人出勤時間</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>

        <FilterPanel
          year={year}
          month={month}
          deptFrom={deptFrom}
          deptTo={deptTo}
          deptFromName={deptFromName}
          deptToName={deptToName}
          loading={loading}
          onYearChange={setYear}
          onMonthChange={setMonth}
          onDeptFromChange={setDeptFrom}
          onDeptToChange={setDeptTo}
          onDeptFromBlur={() => onDeptFromBlur(deptFrom)}
          onDeptToBlur={() => onDeptToBlur(deptTo)}
          onQuery={handleQuery}
          onReset={handleReset}
        />

        <ResultTable
          data={data}
          loading={loading}
          queried={queried}
          noData={noData}
          daysInMonth={daysInMonth}
          queryYear={queryYear}
          queryMonth={queryMonth}
        />
      </div>
    </PermissionGuard>
  );
};

export default P9315Form;
